/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Container,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
  Box,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";

import LocalService from "services/localService";
import LocalMachineService from "services/localMachineService";
import VisitTransactionService from "services/visitTransactionService";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppFormContext from "components/common/AppFormContext";
import AppFormSection from "components/common/AppFormSection";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";
import { buildMachineDisplayName } from "utils/machineDisplayName";

function BulkMoveLocalMachinesForm({
  local,
  localMachineDetails,
  onClose,
  onSuccess,
}) {
  const normalizeLocalType = (type) =>
    String(type || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  const isComercioLocal = normalizeLocalType(local?.Type) === "comercio";

  const [depositos, setDepositios] = React.useState([]);
  const [isLoadingLocals, setIsLoadingLocals] = React.useState(true);

  const [defaultDeposito, setDefaultDeposito] = React.useState(null);
  const [useCustomPerMachine, setUseCustomPerMachine] = React.useState(false);
  const [perMachineDepositos, setPerMachineDepositos] = React.useState({});
  const [effectiveDate, setEffectiveDate] = React.useState(getDefaultOperationDate());

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [showDepositoError, setShowDepositoError] = React.useState(false);
  const [lastCollectionDate, setLastCollectionDate] = React.useState(null);
  const [latestInactiveMachineFinishDate, setLatestInactiveMachineFinishDate] = React.useState(null);
  const [closeDateBounds, setCloseDateBounds] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const [data, lastVisitData, inactiveLocalMachines, bounds] = await Promise.all([
          LocalService.getActiveLocalDepositoMachines(),
          VisitTransactionService.getLastCollectionByLocal(local?.Id),
          LocalMachineService.getInactiveLocalMachineByLocal(local?.Id),
          LocalService.getCloseDateBounds(local?.Id),
        ]);
        if (!mounted) return;
        const boundsMinDate = parseDateOnlyToLocalDate(bounds?.MinEffectiveDate);
        setDepositios(data);
        setLastCollectionDate(
          parseDateOnlyToLocalDate(dateOnlyFromDateTime(lastVisitData?.visit?.DateTime))
        );
        setCloseDateBounds(bounds);
        setLatestInactiveMachineFinishDate(
          (inactiveLocalMachines || [])
            .map((localMachine) =>
              parseDateOnlyToLocalDate(dateOnlyFromDateTime(localMachine?.FinishDateTime))
            )
            .filter(Boolean)
            .reduce((latest, current) => {
              if (!latest) return current;
              return current.getTime() >= latest.getTime() ? current : latest;
            }, null)
        );
        if (boundsMinDate && isBeforeMinOperationDate(effectiveDate, boundsMinDate)) {
          setEffectiveDate(boundsMinDate);
        }
      } catch (error) {
        console.error("[BulkMoveLocalMachinesForm] Error al cargar depósitos:", error);
      } finally {
        if (mounted) setIsLoadingLocals(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [local?.Id]);

  const getLocalLabel = (loc) =>
    loc?.Name ?? loc?.LocalName ?? `Local #${loc?.Id ?? "?"}`;

  const getMachineLabel = (lm) => {
    const type =
      lm?.Machine?.MachineType?.Type ??
      lm?.MachineType?.Type ??
      lm?.MachineTypeLabel ??
      "Tipo desconocido";

    const name = isComercioLocal
      ? buildMachineDisplayName(lm, { fallback: "Sin nombre" })
      : (lm?.Machine?.Name ?? lm?.MachineName ?? "Sin nombre");

    return `${name} · ${type}`;
  };

  const getApiErrorMessage = (error) => {
    const apiError = error?.response?.data?.error;
    const apiCode = error?.response?.data?.code;

    if (apiCode === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de una de las máquinas.";
    }
    if (apiCode === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de alguna de las máquinas del local.";
    }
    if (apiCode === "EFFECTIVE_DATE_BEFORE_LAST_COLLECTION") {
      return "La fecha de cierre no puede ser anterior a la última recaudación del local.";
    }
    if (apiCode === "EFFECTIVE_DATE_BEFORE_LAST_LOCAL_MACHINE_FINISH") {
      return "La fecha de cierre no puede ser anterior al último egreso de una máquina del local.";
    }
    if (apiCode === "LOCAL_CLOSE_BEFORE_COUNTER_CONFIG") {
      return "No se puede cerrar el local con fecha anterior a una configuración de contadores ya registrada.";
    }
    if (apiCode === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }
    if (apiCode === "LOCAL_NOT_ACTIVE") {
      return "El local ya no está activo y no puede cerrarse nuevamente.";
    }
    if (apiCode === "INVALID_TARGET_LOCAL_TYPE") {
      return "El destino debe ser un depósito o taller activo.";
    }
    if (apiCode === "DEFAULT_TARGET_LOCAL_ID_REQUIRED") {
      return "Debes seleccionar un depósito destino para cerrar el local.";
    }

    return apiError || "No se pudo cerrar el local y mover las máquinas. Intenta nuevamente.";
  };

  const handleChangePerMachineDeposito = (localMachineId, deposito) => {
    setPerMachineDepositos((prev) => ({
      ...prev,
      [localMachineId]: deposito,
    }));
  };

  const minEffectiveDate = React.useMemo(() => {
    const dates = (Array.isArray(localMachineDetails) ? localMachineDetails : [])
      .map((lm) => parseDateOnlyToLocalDate(dateOnlyFromDateTime(lm?.StartDateTime)))
      .filter(Boolean);

    if (!dates.length) return null;

    return dates.reduce((latest, current) =>
      current.getTime() > latest.getTime() ? current : latest
    );
  }, [localMachineDetails]);

  const localCreatedAtDate = React.useMemo(
    () => parseDateOnlyToLocalDate(dateOnlyFromDateTime(local?.CreatedAt)),
    [local?.CreatedAt]
  );

  const selectedTargetsMinDate = React.useMemo(() => {
    const selectedDates = [];

    if (defaultDeposito?.CreatedAt) {
      const defaultCreatedAtDate = parseDateOnlyToLocalDate(
        dateOnlyFromDateTime(defaultDeposito.CreatedAt)
      );
      if (defaultCreatedAtDate) selectedDates.push(defaultCreatedAtDate);
    }

    if (useCustomPerMachine) {
      for (const lm of Array.isArray(localMachineDetails) ? localMachineDetails : []) {
        const selectedDeposito = perMachineDepositos[lm?.Id] || defaultDeposito;
        const createdAtDate = parseDateOnlyToLocalDate(
          dateOnlyFromDateTime(selectedDeposito?.CreatedAt)
        );
        if (createdAtDate) selectedDates.push(createdAtDate);
      }
    }

    if (!selectedDates.length) return null;

    return selectedDates.reduce((latest, current) =>
      current.getTime() >= latest.getTime() ? current : latest
    );
  }, [defaultDeposito, localMachineDetails, perMachineDepositos, useCustomPerMachine]);

  const resolvedPickerMinDate = React.useMemo(() => {
    const boundsMinDate = parseDateOnlyToLocalDate(closeDateBounds?.MinEffectiveDate);
    const candidateDates = [
      localCreatedAtDate,
      lastCollectionDate,
      minEffectiveDate,
      latestInactiveMachineFinishDate,
      selectedTargetsMinDate,
      boundsMinDate,
    ].filter(Boolean);

    if (!candidateDates.length) return null;

    return candidateDates.reduce((latest, current) =>
      current.getTime() >= latest.getTime() ? current : latest
    );
  }, [
    localCreatedAtDate,
    lastCollectionDate,
    minEffectiveDate,
    latestInactiveMachineFinishDate,
    selectedTargetsMinDate,
    closeDateBounds,
  ]);

  const effectiveDateHelperText = React.useMemo(() => {
    if (!resolvedPickerMinDate) return "";
    const counterConfigText =
      closeDateBounds?.ReferenceKind === "counter_config"
        ? " No se puede seleccionar una fecha anterior a la última configuración de contadores de las máquinas del local."
        : "";

    return `Fecha minima permitida: ${resolvedPickerMinDate.toLocaleDateString("es-UY")}.${counterConfigText}`;
  }, [closeDateBounds, resolvedPickerMinDate]);

  const getEffectiveDateErrorMessage = React.useCallback(() => {
    const boundsMinDate = parseDateOnlyToLocalDate(closeDateBounds?.MinEffectiveDate);
    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (lastCollectionDate && isBeforeMinOperationDate(effectiveDate, lastCollectionDate)) {
      return "La fecha de cierre no puede ser anterior a la última recaudación del local.";
    }
    if (
      closeDateBounds?.ReferenceKind === "counter_config" &&
      boundsMinDate &&
      isBeforeMinOperationDate(effectiveDate, boundsMinDate)
    ) {
      return "No se puede seleccionar una fecha anterior a la última configuración de contadores de las máquinas del local.";
    }
    if (
      latestInactiveMachineFinishDate &&
      resolvedPickerMinDate &&
      latestInactiveMachineFinishDate.getTime() === resolvedPickerMinDate.getTime() &&
      isBeforeMinOperationDate(effectiveDate, latestInactiveMachineFinishDate)
    ) {
      return "La fecha de cierre no puede ser anterior al último egreso de una máquina del local.";
    }
    if (minEffectiveDate && isBeforeMinOperationDate(effectiveDate, minEffectiveDate)) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de alguna de las máquinas del local.";
    }
    if (localCreatedAtDate && isBeforeMinOperationDate(effectiveDate, localCreatedAtDate)) {
      return "La fecha de cierre no puede ser anterior a la fecha de creación del local.";
    }
    if (selectedTargetsMinDate && isBeforeMinOperationDate(effectiveDate, selectedTargetsMinDate)) {
      return "La fecha de cierre no puede ser anterior a la fecha de creación de alguno de los destinos seleccionados.";
    }
    return "";
  }, [
    effectiveDate,
    lastCollectionDate,
    latestInactiveMachineFinishDate,
    minEffectiveDate,
    localCreatedAtDate,
    resolvedPickerMinDate,
    selectedTargetsMinDate,
    closeDateBounds,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isProcessing) return;

    setErrorMsg("");

    const machines = Array.isArray(localMachineDetails) ? localMachineDetails : [];
    const hasMachines = machines.length > 0;
    const effectiveDateError = getEffectiveDateErrorMessage();

    if (effectiveDateError) {
      return;
    }

    if (hasMachines && !defaultDeposito?.Id) {
      setShowDepositoError(true);
      return;
    }

    const payload = {
      EffectiveDate: toLocalDateOnlyString(effectiveDate),
    };

    if (hasMachines) {
      payload.DefaultTargetLocalId = defaultDeposito.Id;

      if (useCustomPerMachine) {
        const perMachineTargetLocalId = {};

        for (const lm of machines) {
          const lmId = lm.Id;
          if (!lmId) continue;

          const destino = perMachineDepositos[lmId];
          if (destino?.Id && Number(destino.Id) !== Number(defaultDeposito.Id)) {
            perMachineTargetLocalId[lmId] = Number(destino.Id);
          }
        }

        if (Object.keys(perMachineTargetLocalId).length > 0) {
          payload.PerMachineTargetLocalId = perMachineTargetLocalId;
        }
      }
    }

    setIsProcessing(true);
    try {
      const result = await LocalService.closeLocalAndMoveMachines(local.Id, payload);
      await onSuccess?.(result);
    } catch (error) {
      console.error("[BulkMoveLocalMachinesForm] Error en cierre masivo:", error);
      setErrorMsg(getApiErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const totalMaquinas = Array.isArray(localMachineDetails)
    ? localMachineDetails.length
    : 0;
  const hasMachines = totalMaquinas > 0;

  const noHayDepositos = !isLoadingLocals && depositos.length === 0;
  const effectiveDateError = getEffectiveDateErrorMessage();
  const today = getDefaultOperationDate();

  return (
    <Container component={Paper} style={{ padding: "20px", marginTop: "20px" }}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AppFormContext
              title={local?.Name || "Local sin nombre"}
              lines={[hasMachines ? `${totalMaquinas} máquinas activas` : "Sin máquinas activas"]}
            />
          </Grid>
          {hasMachines ? (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  options={depositos}
                  getOptionLabel={getLocalLabel}
                  isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                  value={defaultDeposito}
                  onChange={(_e, value) => {
                    setDefaultDeposito(value);
                    setShowDepositoError(false);
                    setErrorMsg("");
                    if (!value) {
                      setUseCustomPerMachine(false);
                      setPerMachineDepositos({});
                    }
                  }}
                  disabled={isLoadingLocals}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Depósito destino"
                      required
                      placeholder="Selecciona un depósito"
                      error={(showDepositoError && !defaultDeposito) || noHayDepositos}
                      helperText={
                        noHayDepositos
                          ? "No hay depósitos activos."
                          : showDepositoError && !defaultDeposito
                            ? "Seleccioná un depósito destino."
                            : ""
                      }
                    />
                  )}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Este local no tiene máquinas activas. Al confirmar, se cerrará el local con la fecha seleccionada.
              </Typography>
            </Grid>
          )}

          {hasMachines && defaultDeposito && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useCustomPerMachine}
                    onChange={(e) => {
                      setUseCustomPerMachine(e.target.checked);
                      setErrorMsg("");
                    }}
                  />
                }
                label="¿Guardar máquinas en distintos depósitos?"
              />
            </Grid>
          )}

          {hasMachines && useCustomPerMachine && (
            <Grid item xs={12}>
              <AppFormSection title="Destino de las máquinas">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Elegí otro depósito para las máquinas que quieras separar.
                </Typography>
                <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
                {localMachineDetails.map((lm) => {
                  const lmId = lm.Id;
                  const selectedDeposito = perMachineDepositos[lmId] || defaultDeposito;

                  return (
                    <Box
                      key={lmId}
                      sx={{
                        py: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-of-type": { borderBottom: 0, pb: 0 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getMachineLabel(lm)}
                        </Typography>
                      </Box>

                      <Autocomplete
                        options={depositos}
                        getOptionLabel={getLocalLabel}
                        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                        value={selectedDeposito}
                        onChange={(_e, value) => {
                          handleChangePerMachineDeposito(lmId, value);
                          setErrorMsg("");
                        }}
                        disabled={isLoadingLocals || !defaultDeposito}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Depósito destino"
                            required
                            placeholder="Elegí un depósito"
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  );
                })}
                </Box>
              </AppFormSection>
            </Grid>
          )}

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
              <DatePicker
                label="Fecha de cierre del local"
                value={effectiveDate}
                onChange={(value) => {
                  setEffectiveDate(value);
                  setErrorMsg("");
                }}
                minDate={resolvedPickerMinDate || undefined}
                maxDate={today}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(effectiveDateError),
                    helperText: effectiveDateError || effectiveDateHelperText,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {errorMsg && (
            <Grid item xs={12}>
              <Typography variant="caption" color="error">
                {errorMsg}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CancelButton
                  onClick={onClose}
                  disabled={isProcessing}
                  fullWidth
                >
                  Cancelar
                </CancelButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <AcceptButton
                  type="submit"
                  disabled={
                    isProcessing ||
                    Boolean(effectiveDateError) ||
                    (hasMachines && noHayDepositos)
                  }
                  fullWidth
                >
                  {isProcessing ? "Procesando..." : "Cerrar local"}
                </AcceptButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}

BulkMoveLocalMachinesForm.propTypes = {
  local: PropTypes.object.isRequired,
  localMachineDetails: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default BulkMoveLocalMachinesForm;
